import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Loader2, Plus } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/app/components/ui/input';
import { usePermissions } from '@/hooks/use-permissions';
import { cn } from '@/app/components/ui/utils';
import { AddJobDialog } from '@/app/pages/jobs/add-job-dialog';
import type { Project } from '@/lib/types';

export interface ProjectOption {
  id: number;
  name: string;
  project_number: string;
  client_name?: string | null;
  location?: string;
  location_fk?: number | null;
  location_fk_type?: string;
}

interface ProjectSearchComboBoxProps {
  value: ProjectOption | null;
  onChange: (project: ProjectOption | null) => void;
  className?: string;
  required?: boolean;
}

export function ProjectSearchComboBox({
  value,
  onChange,
  className,
  required = false,
}: ProjectSearchComboBoxProps) {
  const { hasMinRole } = usePermissions();
  const canCreateProject = hasMinRole('Manager');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchEnabled = debouncedSearch.length >= 2 && !value;
  const { data, isFetching } = useQuery({
    queryKey: ['projects', 'search', debouncedSearch],
    queryFn: async () => {
      const res = await apiClient.get<{
        results: {
          id: number;
          name: string;
          project_number: string;
          client_name?: string | null;
          location?: string;
          location_fk?: number | null;
          location_fk_type?: string;
        }[];
      }>('/api/v1/projects/projects/', {
        params: { search: debouncedSearch, page_size: 10 },
      });
      return res.data;
    },
    enabled: searchEnabled,
  });

  const results = data?.results ?? [];

  const handleSelect = (project: ProjectOption) => {
    onChange(project);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
  };

  const handleCreated = (project: Project) => {
    handleSelect({
      id: project.id,
      name: project.name,
      project_number: project.project_number,
      client_name: project.client_name,
      location: project.location,
      location_fk: project.location_fk,
      location_fk_type: project.location_fk_type ?? undefined,
    });
  };

  if (value) {
    return (
      <div
        key="selected"
        className={cn('flex items-center gap-3 mt-1 px-3 py-2 border rounded-md bg-gray-50', className)}
        data-field-container={required ? '' : undefined}
        data-required={required ? 'true' : undefined}
        data-required-empty="false"
        data-required-message="Project is required."
        tabIndex={required ? -1 : undefined}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">
            {value.project_number} &mdash; {value.name}
            {value.client_name && (
              <span className="text-gray-500 font-normal"> ({value.client_name})</span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600"
          aria-label="Clear project selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      key="empty"
      ref={containerRef}
      className={cn('mt-1 space-y-1 relative', className)}
      data-field-container={required ? '' : undefined}
      data-required={required ? 'true' : undefined}
      data-required-empty={required ? 'true' : undefined}
      data-required-message="Project is required."
      tabIndex={required ? -1 : undefined}
    >
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          placeholder="Search projects by name or number..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-10"
        />
      </div>
      {isOpen && search.length >= 2 && (
        <>
          {(isFetching || search !== debouncedSearch) && (
            <p className="text-xs text-gray-400 px-1 py-1 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching...
            </p>
          )}
          {!isFetching && search === debouncedSearch && results.length > 0 && (
            <div className="border rounded-md divide-y max-h-48 overflow-y-auto shadow-sm">
              {results.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  className="w-full flex items-start gap-3 px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors"
                  onClick={() => handleSelect(project)}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {project.project_number} &mdash; {project.name}
                      {project.client_name && (
                        <span className="text-gray-500 font-normal"> ({project.client_name})</span>
                      )}
                    </p>
                  </div>
                </button>
              ))}
              {canCreateProject && (
                <button
                  type="button"
                  data-skip-required-validation="true"
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                  onClick={() => setAddProjectOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Create Project
                </button>
              )}
            </div>
          )}
          {!isFetching && search === debouncedSearch && results.length === 0 && (
            <div className="border rounded-md shadow-sm">
              <p className="text-xs text-gray-500 px-3 py-2">No projects found.</p>
              {canCreateProject && (
                <button
                  type="button"
                  data-skip-required-validation="true"
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors border-t"
                  onClick={() => setAddProjectOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  Create Project
                </button>
              )}
            </div>
          )}
        </>
      )}
      {isOpen && search.length > 0 && search.length < 2 && (
        <p className="text-xs text-gray-400 px-1">Type at least 2 characters to search</p>
      )}
      <AddJobDialog
        open={addProjectOpen}
        onOpenChange={setAddProjectOpen}
        onCreated={handleCreated}
      />
    </div>
  );
}
